<script>
export default {
  props: {
    name: { type: String, required: true },
    systemName: { type: String, default: "" },
    description: { type: String, default: "" },
    logo: { type: String, default: "" },
    // Already-localised method labels, e.g. ["Cards", "Apple Pay"].
    methods: { type: Array, default: () => [] },
    actionLabel: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    note: { type: String, default: "" },
  },
  computed: {
    showSystemName() {
      return !!this.systemName && this.systemName.toLowerCase() !== this.name.toLowerCase();
    },
    systemNameLabel() {
      return this.$t("gateways.via_system_name", "via {systemName}", {
        systemName: this.systemName,
      });
    },
  },
  methods: {
    onClick() {
      if (this.disabled) {
        return;
      }
      this.$emit("click");
    },
  },
};
</script>

<template>
  <a href="#" class="ga-row" :class="{ 'ga-row--disabled': disabled }" @click.prevent="onClick">
    <span class="ga-row__logo">
      <img v-if="logo" :src="logo" :alt="name" />
      <span v-else class="ga-row__logo-fallback">{{ name.slice(0, 1) }}</span>
    </span>

    <span class="ga-row__body">
      <span class="ga-row__head">
        <span class="ga-row__name">{{ name }}</span>
        <span v-if="note" class="ga-row__note">{{ note }}</span>
      </span>
      <span v-if="showSystemName" class="ga-row__system">{{ systemNameLabel }}</span>
      <span v-if="description" class="ga-row__desc">{{ description }}</span>
      <span v-if="methods.length" class="ga-row__methods">
        <span v-for="m in methods" :key="m" class="ga-row__method">{{ m }}</span>
      </span>
    </span>

    <span v-if="actionLabel && !disabled" class="ga-row__action">{{ actionLabel }}</span>
  </a>
</template>

<style scoped lang="scss">
.ga-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid rgba(32, 67, 78, 0.15);
  border-radius: 4px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    text-decoration: none;
    border-color: rgba(32, 67, 78, 0.3);
    box-shadow: 0 4px 14px rgba(22, 59, 69, 0.07);
  }

  &--disabled {
    cursor: default;
    background: #fafbfc;
    pointer-events: none;

    .ga-row__logo,
    .ga-row__name {
      opacity: 0.5;
    }
  }

  &__logo {
    flex: none;
    width: 40px;
    height: 40px;
    border-radius: 6px;
    background: #f4f7f9;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    img {
      max-width: 32px;
      max-height: 32px;
      object-fit: contain;
    }
  }

  &__logo-fallback {
    font-family: $vito-bold;
    font-size: 16px;
    color: #6b7686;
  }

  &__body {
    flex: 1;
    min-width: 0;
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__name {
    font-family: $vito-bold;
    font-size: 15px;
    color: #20434e;
  }

  &__note {
    font-family: $vito-bold;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: #b06a12;
    background: #fdf3e2;
    border-radius: 3px;
    padding: 1px 6px;
  }

  &__system {
    display: block;
    margin-top: 1px;
    font-size: 12px;
    color: #7c8798;
  }

  &__desc {
    display: block;
    margin-top: 3px;
    font-size: 13px;
    line-height: 1.45;
    color: #667079;
  }

  &__methods {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 7px;
  }

  &__method {
    font-size: 11px;
    color: #5c6b73;
    background: #f1f4f6;
    border: 1px solid #e6ebee;
    border-radius: 4px;
    padding: 1px 7px;
    white-space: nowrap;
  }

  &__action {
    flex: none;
    align-self: center;
    font-family: $vito-bold;
    font-size: 13px;
    color: $bb-100;
    white-space: nowrap;
  }
}
</style>
